import type { PaginationState, SortingState } from '@tanstack/react-table';

import type { TableColumn } from '../components/Table';
import type { FilterGroupItem, FilterLineItem, FilterType } from '../utils/filterSettings';
import { NULL_OPERATOR_IDS } from '../utils/filterSettings';

import type {
  ServerFilter,
  ServerFilterGroup,
  ServerFilterLine,
  TablePagination,
  TableSort,
} from './useTableStateControl.types';

/**
 * Convert TanStack Table's SortingState to TableSort format.
 * An empty sorting state stays `[]` so a cleared sort is distinct from a missing one.
 */
export function convertSortingStateToTableSort(sorting: SortingState): TableSort[] {
  if (sorting.length === 0) return [];

  const firstSort = sorting[0];
  return [
    {
      property: firstSort.id,
      order: firstSort.desc ? 'desc' : 'asc',
    },
  ];
}

/**
 * Convert TableSort format to TanStack Table's SortingState
 */
export function convertTableSortToSortingState(sort: TableSort[]): SortingState {
  return sort.map((s) => ({ id: s.property, desc: s.order === 'desc' }));
}

/**
 * Resolve the active sorting state from a URL sort param, falling back to a caller-supplied
 * default only when the URL carries no sort (e.g. on first visit). An empty array is a sort
 * the user cleared and resolves to no sorting.
 */
export function resolveSortingState(sort: TableSort[] | undefined, defaultSorting: SortingState = []): SortingState {
  return sort ? convertTableSortToSortingState(sort) : defaultSorting;
}

/**
 * Convert TanStack Table's PaginationState to TablePagination format
 */
export function convertPaginationStateToTablePagination(pagination: PaginationState): TablePagination {
  return {
    limit: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  };
}

/**
 * Convert TablePagination format to TanStack Table's PaginationState
 */
export function convertTablePaginationToPaginationState(pagination: TablePagination): PaginationState {
  return {
    pageIndex: Math.floor(pagination.skip / pagination.limit),
    pageSize: pagination.limit,
  };
}

/**
 * Convert FilterGroupItem (UI format) to ServerFilterGroup (server format)
 */
export function convertFilterGroupItemToServer(group: FilterGroupItem): ServerFilterGroup {
  const filters: ServerFilter[][] = [];

  for (const item of group.items) {
    if (
      item.type === 'line' &&
      item.filterType &&
      item.operatorType &&
      (item.value !== undefined || NULL_OPERATOR_IDS.has(item.operatorType.id))
    ) {
      // Convert line item to server filter line
      const serverLine: ServerFilterLine = {
        property: item.filterType.id,
        operator: item.operatorType.id,
        value: item.value,
      };
      // Wrap in array (server format expects array of arrays)
      filters.push([serverLine]);
    } else if (item.type === 'group') {
      // Recursively convert nested group
      const nestedGroup = convertFilterGroupItemToServer(item);
      // Wrap the nested group in an array (ServerFilterGroup is a ServerFilter)
      filters.push([nestedGroup]);
    }
  }

  return {
    filterType: group.operator || 'and',
    filters,
  };
}

/**
 * Convert ServerFilterGroup (server format) to FilterGroupItem (UI format)
 */
export function convertServerToFilterGroupItem(
  serverGroup: ServerFilterGroup,
  filterTypes: FilterType[],
): FilterGroupItem {
  const items: (FilterLineItem | FilterGroupItem)[] = [];

  // Server filters is an array of arrays
  const filterArrays = Array.isArray(serverGroup.filters) ? serverGroup.filters : [];

  for (const filterArray of filterArrays) {
    if (!Array.isArray(filterArray)) continue;

    for (const filter of filterArray) {
      if ('filterType' in filter && 'filters' in filter) {
        // This is a nested group
        items.push({
          ...convertServerToFilterGroupItem(filter as ServerFilterGroup, filterTypes),
          id: `group-${Date.now()}-${Math.random()}`,
        });
      } else if ('property' in filter && 'operator' in filter) {
        const serverLine = filter as ServerFilterLine;
        // When building the filter line, we need to match the column definition from filterTypes to provide the hydrated data
        // Fine matching column definition from filterTypes
        const column = filterTypes.find((ft) => ft.id === serverLine.property);
        const operatorType = column?.filterOperatorTypeConfig?.find((ot) => ot.id === serverLine.operator);
        // This is a filter line
        const lineItem: FilterLineItem = {
          type: 'line',
          id: `line-${Date.now()}-${Math.random()}`,
          operator: 'and', // Default operator for lines within a group
          filterType: {
            id: serverLine.property,
            label: column?.label || serverLine.property,
            filterValueConfig: column?.filterValueConfig,
            filterOperatorTypeConfig: column?.filterOperatorTypeConfig,
          },
          operatorType: {
            id: serverLine.operator,
            label: operatorType?.label || serverLine.operator,
          },
          value: String(serverLine.value ?? ''),
        };
        items.push(lineItem);
      }
    }
  }

  return {
    type: 'group',
    id: 'root',
    operator: serverGroup.filterType || 'and',
    items,
  };
}

/**
 * Enrich a FilterGroupItem by merging each line's filterType with the matching FilterType
 * from filterTypes (adds filterValueConfig, filterOperatorTypeConfig). Used when filters
 * are stored without full type info (e.g. Valtio store) so display can show dates/select labels.
 */
export function enrichFilterGroupWithFilterTypes(group: FilterGroupItem, filterTypes: FilterType[]): FilterGroupItem {
  const items = group.items.map((item) => {
    if (item.type === 'group') {
      return enrichFilterGroupWithFilterTypes(item, filterTypes);
    }
    const line = item as FilterLineItem;
    const matched = filterTypes.find((ft) => ft.id === line.filterType?.id);
    if (!matched) return line;
    return {
      ...line,
      filterType: {
        ...line.filterType,
        id: line.filterType?.id ?? '',
        label: matched.label ?? line.filterType?.label ?? line.filterType?.id,
        filterValueConfig: matched.filterValueConfig ?? line.filterType?.filterValueConfig,
        filterOperatorTypeConfig: matched.filterOperatorTypeConfig ?? line.filterType?.filterOperatorTypeConfig,
      },
    };
  });
  return { ...group, items };
}

/**
 * Create an empty filter group
 */
export function createEmptyFilterGroup(): FilterGroupItem {
  return {
    type: 'group',
    id: 'root',
    operator: 'and',
    items: [],
  };
}

/**
 * Check if a filter group is empty (no filters applied)
 */
export function isEmptyFilterGroup(group: FilterGroupItem): boolean {
  return group.items.length === 0;
}

/**
 * Get all column filters from the root level of filters
 * Returns an array of filters for the specified column
 */
export function getColumnFilter(filters: FilterGroupItem, columnId: string): FilterLineItem[] {
  return filters.items.filter(
    (item): item is FilterLineItem => item.type === 'line' && item.filterType?.id === columnId,
  );
}

/**
 * Add a new filter for a column at the root level of filters
 * Keeps existing filters for the same column (supports multiple filters per column)
 * Note: accessor is not included in the property - it's just UI metadata
 * The server-side PROPERTY_ACCESSORS mapping handles nested value extraction
 */
export function setColumnFilter(
  filters: FilterGroupItem,
  columnId: string,
  operatorType: { id: string; label: string },
  value: string,
): FilterGroupItem {
  // Add new filter (property is just the columnId, not columnId.accessor)
  const newFilter: FilterLineItem = {
    type: 'line',
    id: `line-${Date.now()}-${Math.random()}`,
    operator: 'and',
    filterType: {
      id: columnId, // Just the column ID, server uses PROPERTY_ACCESSORS for nested values
      label: columnId, // Label as column ID for UI display
    },
    operatorType,
    value,
  };

  return {
    ...filters,
    items: [...filters.items, newFilter],
  };
}

/**
 * Remove a specific filter by its ID from the root level of filters
 */
export function removeColumnFilter(filters: FilterGroupItem, filterId: string): FilterGroupItem {
  return {
    ...filters,
    items: filters.items.filter((item) => item.id !== filterId),
  };
}

/**
 * Derive FilterType array from table columns
 * Each column becomes a filterable option, except columns with `enableColumnFilter: false`
 * (e.g. client-computed columns a server-side table can't filter on)
 * Includes filterValueConfig and filterOperatorTypeConfig from column.meta if available
 */
export function deriveFilterTypesFromColumns<TData>(columns: TableColumn<TData>[]): FilterType[] {
  return columns
    .filter((col): col is typeof col & { id: string } => !!col.id && col.id !== 'select')
    .filter((col) => col.enableColumnFilter !== false)
    .map((col) => ({
      id: col.id,
      label: typeof col.header === 'string' ? col.header : col.id,
      // Extract filterValueConfig from meta if available
      filterValueConfig: (col.meta as any)?.filterValueConfig,
      // Extract filterOperatorTypeConfig from meta if available
      filterOperatorTypeConfig: (col.meta as any)?.filterOperatorTypeConfig,
      combineOrGroups: (col.meta as any)?.combineOrGroups,
    }));
}

/**
 * API Filter structure - matches common API filter format
 * Used for building search payloads from table state
 */
export type ApiFilter = {
  property: string;
  operator: string;
  value: string | number | boolean;
};

/**
 * API Filter Group - can contain nested filters with and/or operators
 * Used for building search payloads from table state
 */
export type ApiFilterGroup = {
  filterType: 'and' | 'or';
  filters: (ApiFilter | ApiFilterGroup)[];
};

/**
 * API Sort configuration
 * Used for building search payloads from table state
 */
export type ApiSort = {
  property: string;
  order: 'asc' | 'desc';
};

/**
 * Search payload structure for API requests
 * Built from table state (pagination, sorting, filters, globalFilter)
 */
export type SearchPayload = {
  limit: number;
  skip: number;
  sort?: ApiSort[];
  filters?: ApiFilterGroup[];
  globalFilter?: string;
};

/**
 * Transform FilterGroupItem (UI format) to ApiFilterGroup (API format)
 * This is a simpler format than ServerFilterGroup - uses flat arrays instead of nested arrays
 */
export function transformFiltersToApi(
  filters: FilterGroupItem,
  { noValueOperators = NULL_OPERATOR_IDS }: { noValueOperators?: Set<string> } = {},
): ApiFilterGroup | null {
  if (!filters.items || filters.items.length === 0) {
    return null;
  }

  const apiFilters: (ApiFilter | ApiFilterGroup)[] = [];

  for (const item of filters.items) {
    if (item.type === 'line') {
      const lineItem = item as FilterLineItem;
      const operatorId = lineItem.operatorType?.id;
      const valueRequired = !operatorId || !noValueOperators.has(operatorId);
      // Only include complete filter lines (with field, operator, and value — unless the
      // operator needs no value)
      if (lineItem.filterType?.id && operatorId && (!valueRequired || lineItem.value)) {
        apiFilters.push({
          property: lineItem.filterType.id,
          operator: operatorId,
          value: lineItem.value as string,
        });
      }
    } else if (item.type === 'group') {
      // Recursively transform nested groups
      const nestedGroup = transformFiltersToApi(item, { noValueOperators });
      if (nestedGroup) {
        apiFilters.push(nestedGroup);
      }
    }
  }

  if (apiFilters.length === 0) {
    return null;
  }

  return {
    filterType: filters.operator || 'and',
    filters: apiFilters,
  };
}

/**
 * Build search payload from table state
 * Converts table state (pagination, sorting, filters, globalFilter) into API search payload format
 */
export function buildSearchPayload(params: {
  pageIndex: number;
  pageSize: number;
  sorting?: { id: string; desc: boolean }[];
  filters?: FilterGroupItem;
  globalFilter?: string;
}): SearchPayload {
  const { pageIndex, pageSize, sorting, filters, globalFilter } = params;

  const payload: SearchPayload = {
    limit: pageSize,
    skip: pageIndex * pageSize,
  };

  // Add sorting
  if (sorting && sorting.length > 0) {
    const sort = sorting[0];
    payload.sort = [
      {
        property: sort.id,
        order: sort.desc ? 'desc' : 'asc',
      },
    ];
  }

  // Add filters
  if (filters) {
    const apiFilter = transformFiltersToApi(filters, { noValueOperators: NULL_OPERATOR_IDS });
    if (apiFilter) {
      payload.filters = [apiFilter];
    }
  }

  // Add global filter
  if (globalFilter?.trim()) {
    payload.globalFilter = globalFilter;
  }

  return payload;
}
